/**
 * Label disambiguation shared by every chart-config builder (ChartBuilderDialog
 * and the dashboard add-tile form). `ChartConfig` columns hold the ROW key
 * (`col_<i>` for a duplicate/blank column); a `label` rides along when the
 * display name differs so legend/axis/tooltip copy matches the picker. But
 * two DIFFERENT keys can share one display name (`SELECT id, id` → keys
 * `col_0`/`col_1`, both named "id"), and chartDataShaping.ts derives its
 * `xDataKey` and series keys from that label — so an X and a Y column (or two
 * Y columns) picked from same-named source columns would collide into one
 * data key and silently overwrite each other's values. Disambiguating labels
 * once here, at submit time, keeps every downstream key distinct without
 * chartDataShaping.ts having to know about row keys at all.
 */
import type { ColumnTypeInfo } from '../types/chartTypes';

/**
 * Appends " (2)", " (3)" … to each repeated label after its first occurrence.
 * Order-preserving and pure — pass one array per config (X label first, then
 * every Y label in picker order) so the result lines up 1:1 with the input.
 *
 * Every ORIGINAL label is reserved up front (a repeat can't generate a
 * candidate that collides with some *later* original — e.g. picking Y
 * columns named `id`, `id`, `id (2)` must not produce two `id (2)` series),
 * and each generated label is reserved as it is minted (so three `id`s don't
 * collide with each other either). When a candidate is reserved, the suffix
 * keeps bumping — (2), (3), (4) … — until it finds a free one.
 */
export function disambiguateChartLabels(labels: string[]): string[] {
  const reserved = new Set(labels);
  const occurrences = new Map<string, number>();
  const result: string[] = [];
  for (const label of labels) {
    const occurrence = (occurrences.get(label) ?? 0) + 1;
    occurrences.set(label, occurrence);
    if (occurrence === 1) {
      result.push(label);
      continue;
    }
    let suffix = occurrence;
    let candidate = `${label} (${suffix})`;
    while (reserved.has(candidate)) {
      suffix += 1;
      candidate = `${label} (${suffix})`;
    }
    reserved.add(candidate);
    result.push(candidate);
  }
  return result;
}

/**
 * Builds the `{ label }` overrides a `ChartConfig`'s `xAxis`/`yAxis` entries
 * attach on submit: one per row key, in submit order (X first, then each Y).
 * Looks up each key's display name via `columnInfos`, disambiguates the whole
 * ordered set, and returns `undefined` for a position whose final label still
 * equals its own key (no override needed — the common, non-duplicate case).
 */
export function labelOverridesForKeys(
  keys: string[],
  columnInfos: readonly Pick<ColumnTypeInfo, 'key' | 'name'>[],
): Array<{ label: string } | undefined> {
  const names = keys.map((key) => columnInfos.find((c) => c.key === key)?.name ?? key);
  const disambiguated = disambiguateChartLabels(names);
  return keys.map((key, i) => (disambiguated[i] !== key ? { label: disambiguated[i] } : undefined));
}
